import type {
    DashboardData,
    ExpiringItemView,
    GroceryItemView,
    GroceryMatchView,
    RecipeSuggestion,
} from "./types/dashboard";
import { handleAuthenticationRequired } from "./auth";
import { getActiveHouseholdId } from "./households";
import { compressImageForUpload } from "./utils/compressImage";
import * as clientDb from "./db/clientDb";
import * as aiCache from "./services/aiCache";
import * as offlineFallback from "./services/offlineFallback";
import * as mutations from "./sync/mutations";
import { performSync } from "./sync/syncEngine";

const UPLOAD_PREP_MAX = {
    location: 768,
    item: 1280,
} as const;

export interface Location {
    id: number;
    name: string;
    image_path: string | null;
    sort_order: number;
    version: number;
    field_timestamps: Record<string, string> | null;
    created_at: string;
}

export interface Item {
    id: number;
    location_id: number;
    name: string;
    brand: string | null;
    quantity: number;
    notes: string | null;
    expiry_date: string | null;
    image_path: string | null;
    barcode: string | null;
    category: string | null;
    created_at: string | null;
    updated_at: string;
    version: number;
    field_timestamps: Record<string, string> | null;
    location_name?: string;
}

export interface BarcodeProduct {
    name: string | null;
    brand: string | null;
    imageUrl: string | null;
    category: string | null;
    cached?: boolean;
}

export interface ItemFilters {
    location?: number;
    category?: string;
    q?: string;
    expiringWithin?: number;
    stocked?: boolean;
    expired?: boolean;
}

export type { DashboardData, GroceryItemView, GroceryMatchView, RecipeSuggestion };

class ApiError extends Error {
    constructor(
        message: string,
        public status: number,
    ) {
        super(message);
    }
}

export class AuthenticationError extends ApiError {}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
    const householdId = getActiveHouseholdId();
    const res = await fetch(path, {
        ...options,
        credentials: "include",
        headers: {
            ...(options.body instanceof FormData ? {} : { "Content-Type": "application/json" }),
            ...(householdId !== null ? { "X-Household-Id": String(householdId) } : {}),
            ...options.headers,
        },
    });

    if (!res.ok) {
        let message = res.statusText;
        let errorCode: string | undefined;
        try {
            const data = await res.json();
            message = data.error || message;
            errorCode = typeof data.error === "string" ? data.error : undefined;
        } catch {
            /* ignore */
        }

        if (res.status === 401 && errorCode === "authentication_required") {
            handleAuthenticationRequired();
            throw new AuthenticationError(message, res.status);
        }

        if (res.status === 403 && errorCode === "not_a_member") {
            void import("./households").then((m) => m.refreshMemberships());
        }

        throw new ApiError(message, res.status);
    }

    if (res.status === 204) return undefined as T;
    return res.json();
}

export interface SyncMutation {
    type: string;
    recordId: number | string;
    fields: Record<string, unknown>;
    clientTimestamp: string;
}

export interface SyncBatchResponse {
    cursor: number;
    items: Item[];
    locations: Location[];
    grocery_items: GroceryItemView[];
    deleted_items: number[];
    deleted_locations: number[];
    deleted_grocery: number[];
    idMap: Record<string, number>;
}

async function bootstrapIfNeeded(): Promise<void> {
    const existing = await clientDb.listItems();
    if (existing.length > 0) return;
    try {
        const res = await api.sync.batch(0, []);
        for (const item of res.items) await clientDb.putItem(item as clientDb.LocalItem);
        for (const location of res.locations)
            await clientDb.putLocation(location as clientDb.LocalLocation);
        for (const g of res.grocery_items) await clientDb.putGroceryItem(g as clientDb.GroceryItem);
        if (res.cursor) {
            await clientDb.setSyncState("syncCursor", String(res.cursor));
        }
        const cached = await clientDb.listCachedImagePaths();
        const toCache = new Set<string>();
        for (const item of res.items) {
            if (item.image_path && !cached.has(item.image_path)) toCache.add(item.image_path);
        }
        for (const loc of res.locations) {
            if (loc.image_path && !cached.has(loc.image_path)) toCache.add(loc.image_path);
        }
        await Promise.all(
            [...toCache].map(async (path) => {
                try {
                    const resp = await fetch(path);
                    if (!resp.ok) return;
                    const blob = await resp.blob();
                    await clientDb.putCachedImage(path, blob);
                } catch {
                    /* offline — will retry on sync */
                }
            }),
        );
    } catch {
        /* offline or auth — will retry on sync */
    }
}

export const api = {
    health: () => request<{ ok: boolean }>("/api/health"),

    sync: {
        batch: (cursor: number, mutations: SyncMutation[]) =>
            request<SyncBatchResponse>("/api/sync", {
                method: "POST",
                body: JSON.stringify({ cursor, mutations }),
            }),
    },

    locations: {
        list: async () => {
            await bootstrapIfNeeded();
            const locations = await clientDb.listLocations();
            return locations.sort((a, b) => a.sort_order - b.sort_order);
        },
        create: async (data: { name: string; image_path?: string | null }, image?: Blob) => {
            const location = await mutations.createLocation(data, image);
            void performSync();
            return location;
        },
        update: async (id: number, data: Partial<Location>, image?: Blob) => {
            const location = await mutations.updateLocation(id, data, image);
            void performSync();
            return location;
        },
        delete: async (id: number) => {
            await mutations.deleteLocation(id);
            void performSync();
        },
    },

    items: {
        list: async (filters: ItemFilters = {}) => {
            await bootstrapIfNeeded();
            const [rawItems, locations] = await Promise.all([
                clientDb.listItems(),
                clientDb.listLocations(),
            ]);
            const locationMap = new Map(locations.map((l) => [l.id, l.name]));
            let items = rawItems;
            for (const item of items) {
                if (!item.location_name && item.location_id) {
                    item.location_name = locationMap.get(item.location_id);
                }
            }
            if (filters.location) items = items.filter((i) => i.location_id === filters.location);
            if (filters.category) items = items.filter((i) => i.category === filters.category);
            if (filters.q) {
                const q = filters.q.toLowerCase();
                items = items.filter(
                    (i) =>
                        i.name.toLowerCase().includes(q) ||
                        (i.brand?.toLowerCase().includes(q) ?? false) ||
                        (i.notes?.toLowerCase().includes(q) ?? false) ||
                        (i.barcode?.toLowerCase().includes(q) ?? false) ||
                        (i.category?.toLowerCase().includes(q) ?? false) ||
                        (i.location_name?.toLowerCase().includes(q) ?? false),
                );
            }
            if (filters.expiringWithin !== undefined) {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const cutoff = new Date();
                cutoff.setDate(cutoff.getDate() + filters.expiringWithin);
                items = items.filter((i) => {
                    if (!i.expiry_date) return false;
                    const expiry = new Date(i.expiry_date + "T00:00:00");
                    return expiry >= today && expiry <= cutoff;
                });
            }

            if (filters.stocked !== undefined) {
                items = items.filter((i) => (filters.stocked ? i.quantity > 0 : i.quantity === 0));
            }

            if (filters.expired) {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                items = items.filter((i) => {
                    if (!i.expiry_date) return false;
                    const expiry = new Date(i.expiry_date + "T00:00:00");
                    return expiry < today;
                });
            }
            return items;
        },
        get: async (id: number) => {
            await bootstrapIfNeeded();
            const item = await clientDb.getItem(id);
            if (item && item.location_id && !item.location_name) {
                const locations = await clientDb.listLocations();
                item.location_name = locations.find((l) => l.id === item.location_id)?.name;
            }
            return item;
        },
        create: async (
            data: Partial<Item> & { location_id: number; name: string },
            image?: Blob,
        ) => {
            const item = await mutations.createItem(
                data as Omit<Item, "id" | "version" | "updated_at">,
                image,
            );
            await performSync();
            return item;
        },
        update: async (id: number, data: Partial<Item>, image?: Blob) => {
            const item = await mutations.updateItem(id, data, image);
            await performSync();
            return item;
        },
        decrement: async (id: number) => {
            const item = await mutations.decrementItem(id);
            void performSync();
            return { item, deleted: false };
        },
        delete: async (id: number) => {
            await mutations.deleteItem(id);
            void performSync();
        },
        categories: async () => {
            const items = await clientDb.listItems();
            const cats = new Set<string>();
            for (const item of items) {
                if (item.category) cats.add(item.category);
            }
            return [...cats].sort();
        },
    },

    barcode: {
        lookup: (upc: string) => request<BarcodeProduct>(`/api/barcode/${encodeURIComponent(upc)}`),
        findItems: (upc: string) =>
            request<Item[]>(`/api/barcode/${encodeURIComponent(upc)}/items`),
    },

    upload: {
        location: async (file: File) => {
            const prepared = await compressImageForUpload(file, UPLOAD_PREP_MAX.location);
            const fd = new FormData();
            fd.append("image", prepared);
            return request<{ path: string }>("/api/upload/location", { method: "POST", body: fd });
        },
        item: async (file: File) => {
            const prepared = await compressImageForUpload(file, UPLOAD_PREP_MAX.item);
            const fd = new FormData();
            fd.append("image", prepared);
            return request<{ path: string }>("/api/upload/item", { method: "POST", body: fd });
        },
    },

    dashboard: {
        get: () => request<DashboardData>("/api/dashboard"),
        getStats: async () => {
            await bootstrapIfNeeded();
            const items = await clientDb.listItems();
            const locations = await clientDb.listLocations();
            const grocery = await clientDb.listGroceryItems();

            const today = new Date();
            today.setHours(0, 0, 0, 0);

            let totalItems = 0;
            let expiredCount = 0;
            let expiringSoonCount = 0;
            const expiringItems: ExpiringItemView[] = [];

            for (const item of items) {
                if (item.quantity <= 0) continue;
                totalItems += item.quantity;
                let daysLeft: number | null = null;
                if (item.expiry_date) {
                    const expiry = new Date(item.expiry_date + "T00:00:00");
                    daysLeft = Math.ceil(
                        (expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
                    );
                    if (daysLeft < 0) {
                        expiredCount += item.quantity;
                    } else if (daysLeft <= 7) {
                        expiringSoonCount += item.quantity;
                    }
                }
                if (daysLeft !== null && daysLeft <= 7) {
                    const location = locations.find((l) => l.id === item.location_id);
                    expiringItems.push({
                        id: item.id,
                        name: item.name,
                        location_name: location?.name,
                        expiry_date: item.expiry_date,
                        image_path: item.image_path,
                        quantity: item.quantity,
                        daysLeft,
                    });
                }
            }

            expiringItems.sort((a, b) => {
                if (a.daysLeft === null) return 1;
                if (b.daysLeft === null) return -1;
                return a.daysLeft - b.daysLeft;
            });

            const outOfStock = grocery.filter((g) => g.active && !g.stocked).length;

            return {
                stats: { totalItems, expiredCount, expiringSoonCount, outOfStock },
                expiringItems,
            };
        },
        getGreeting: async () => {
            const now = new Date();
            const ctx = await offlineFallback.buildGreetingInput(now);

            if (!navigator.onLine) {
                const cached = await aiCache.getCachedGreeting(ctx, now.getTime());
                if (cached)
                    return { greeting: cached, greetingSlot: ctx.slotKey, greetingCached: true };
                return {
                    greeting: offlineFallback.fallbackGreeting(ctx),
                    greetingSlot: ctx.slotKey,
                    greetingCached: false,
                };
            }

            try {
                const data = await request<{
                    greeting: { headline: string; line: string };
                    greetingSlot: string;
                    greetingCached: boolean;
                    greetingGeneratedAt: string;
                }>("/api/dashboard/greeting");
                await aiCache.setCachedGreeting(data.greeting, data.greetingGeneratedAt, ctx);
                return {
                    greeting: data.greeting,
                    greetingSlot: data.greetingSlot,
                    greetingCached: data.greetingCached,
                };
            } catch (err) {
                if (err instanceof AuthenticationError) throw err;
                if ((err instanceof ApiError && err.status === 503) || err instanceof TypeError) {
                    const cached = await aiCache.getCachedGreeting(ctx, now.getTime());
                    if (cached)
                        return {
                            greeting: cached,
                            greetingSlot: ctx.slotKey,
                            greetingCached: true,
                        };
                    return {
                        greeting: offlineFallback.fallbackGreeting(ctx),
                        greetingSlot: ctx.slotKey,
                        greetingCached: false,
                    };
                }
                throw err;
            }
        },
        getVital: async () => {
            const now = new Date();
            const ctx = await offlineFallback.buildGreetingInput(now);

            if (!navigator.onLine) {
                const cached = await aiCache.getCachedVital(ctx, now.getTime());
                if (cached) return { vital: cached, vitalCached: true };
                return { vital: offlineFallback.fallbackVital(ctx), vitalCached: false };
            }

            try {
                const data = await request<{
                    vital: { value: number; label: string };
                    vitalCached: boolean;
                    vitalGeneratedAt: string;
                }>("/api/dashboard/vital");
                await aiCache.setCachedVital(data.vital, data.vitalGeneratedAt, ctx);
                return { vital: data.vital, vitalCached: data.vitalCached };
            } catch (err) {
                if (err instanceof AuthenticationError) throw err;
                if ((err instanceof ApiError && err.status === 503) || err instanceof TypeError) {
                    const cached = await aiCache.getCachedVital(ctx, now.getTime());
                    if (cached) return { vital: cached, vitalCached: true };
                    return { vital: offlineFallback.fallbackVital(ctx), vitalCached: false };
                }
                throw err;
            }
        },
        getRecipe: async () => {
            const now = new Date();
            const candidates = await offlineFallback.getRecipeCandidates(100);

            if (!navigator.onLine) {
                const cached = await aiCache.getCachedRecipe(candidates, now.getTime());
                if (cached)
                    return {
                        recipe: cached,
                        candidateIds: candidates.map((c) => c.id),
                        recipeCached: true,
                    };
                const recipe = offlineFallback.fallbackRecipe(candidates);
                return { recipe, candidateIds: candidates.map((c) => c.id), recipeCached: false };
            }

            try {
                const data = await request<{
                    recipe: RecipeSuggestion | null;
                    recipeCached: boolean;
                    candidateIds: number[];
                    recipeGeneratedAt: string;
                }>("/api/dashboard/recipe");
                if (data.recipe) {
                    await aiCache.setCachedRecipe(data.recipe, data.recipeGeneratedAt, candidates);
                }
                return {
                    recipe: data.recipe,
                    candidateIds: data.candidateIds,
                    recipeCached: data.recipeCached,
                };
            } catch (err) {
                if (err instanceof AuthenticationError) throw err;
                if ((err instanceof ApiError && err.status === 503) || err instanceof TypeError) {
                    const cached = await aiCache.getCachedRecipe(candidates, now.getTime());
                    if (cached)
                        return {
                            recipe: cached,
                            candidateIds: candidates.map((c) => c.id),
                            recipeCached: true,
                        };
                    const recipe = offlineFallback.fallbackRecipe(candidates);
                    return {
                        recipe,
                        candidateIds: candidates.map((c) => c.id),
                        recipeCached: false,
                    };
                }
                throw err;
            }
        },
        spinRecipe: () =>
            request<{ recipe: RecipeSuggestion | null; candidateIds: number[] }>(
                "/api/dashboard/recipe/spin",
                { method: "POST" },
            ),
        useRecipe: (itemIds: number[]) =>
            request<{ ok: boolean }>("/api/dashboard/recipe/use", {
                method: "POST",
                body: JSON.stringify({ itemIds }),
            }),
    },

    grocery: {
        list: async () => {
            await bootstrapIfNeeded();
            const [groceries, items] = await Promise.all([
                clientDb.listGroceryItems(),
                clientDb.listItems(),
            ]);
            const itemMap = new Map(items.map((i) => [i.id, i]));
            return groceries.map((g) => {
                const linked = g.item_id ? itemMap.get(g.item_id) : undefined;
                const itemQty = linked ? linked.quantity : null;
                const minQty = g.min_quantity ?? 1;
                const active = g.mode === "manual" ? !g.stocked : (itemQty ?? 0) < minQty;
                return {
                    ...g,
                    item_name: linked?.name ?? null,
                    item_quantity: itemQty,
                    active,
                } as GroceryItemView;
            });
        },
        create: async (data: {
            name?: string;
            mode: "manual" | "auto";
            item_id?: number;
            min_quantity?: number;
            notes?: string | null;
        }) => {
            const grocery = await mutations.createGrocery({
                name: data.name ?? "",
                mode: data.mode,
                item_id: data.item_id ?? null,
                min_quantity: data.min_quantity ?? null,
                notes: data.notes ?? null,
            });
            void performSync();
            return grocery as GroceryItemView;
        },
        update: async (
            id: number,
            data: Partial<
                Pick<GroceryItemView, "name" | "min_quantity" | "notes" | "mode" | "stocked"> & {
                    item_id?: number;
                }
            >,
        ) => {
            const grocery = await mutations.updateGrocery(id, data);
            void performSync();
            return grocery as GroceryItemView;
        },
        findMatches: (itemId: number) =>
            request<GroceryMatchView[]>("/api/grocery/matches", {
                method: "POST",
                body: JSON.stringify({ item_id: itemId }),
            }),
        link: (groceryId: number, itemId: number) =>
            request<GroceryItemView>(`/api/grocery/${groceryId}`, {
                method: "PATCH",
                body: JSON.stringify({ item_id: itemId, mode: "auto" }),
            }),
        delete: async (id: number) => {
            await mutations.deleteGrocery(id);
            void performSync();
        },
    },
};

export { ApiError };
